import { Button, CircularProgress, Container, Grid } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Box } from "@mui/system";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactLoading from "react-loading";
import { useLocation } from "react-router-dom";
import GoogleSwiper from "../Swiper";
import { PlanModal } from "../v2/enrollment/PlanModal";
import { ErrorModal } from "../v2/enrollment/ErrorModal";
import {
  PROMO_CODE,
  PROVIDER_ID,
  PROVIDER_NAME,
  REPs,
  Tier,
} from "../../config";
import {
  ACPlans,
  ACRows,
  AddressProps,
  ChartDataTypes,
  PlanProps,
  UtilityTypes,
} from "../../types";
import useIpZipCode from "../../hooks/useIpZipCode";
import {
  CNP_CODES,
  DM_CODES,
  LPL_CODES,
  ONCOR_CODES,
  getBestRate,
  getBestValue,
} from "../data";
import {
  fetchConstellationPlans,
  fetchFliterMonth,
  fetchPlan,
  fetchThreeHeightsPlan,
  fetchUtility,
} from "../../fetch/fetch";
import {
  ChoiceSection,
  EvaluateSection,
  HomeIntroContentSection,
  HomeIntroSection,
  IntroContentSection,
  IntroSection,
  SectionWrapper,
  TickGrid,
  TickSection,
} from "../styles/saving-section.styled";
import { EntrySection } from "../styles/common.styled";
import { ChartSection } from "../styles/v2/saving-section.styled";
import { withSnackbar } from "../SnackbarHOC";
import { ACGridContainer } from "../styles/compare.styled";
import { RollingNumber } from "../RollingNumber";
import CompareGrid from "./CompareGrid";
import { CompareGridContext } from "../../context-manager";
import DMGrid from "./dm-grid";

const DmPage = ({
  snackbarShowMessage,
}: {
  snackbarShowMessage?: Function;
}) => {
  const location = useLocation();
  const [zipcode, setZipcode] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("12");
  const [utilities, setUtilities] = useState<UtilityTypes[]>([]);
  const [selectedUtility, setSelectedUtility] = useState("");
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartDataTypes[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [oneRepPlanList, setOneRepPlanList] = useState<PlanProps[]>([]);
  const [plans, setPlans] = useState<
    { [key: string]: PlanProps[] } | undefined
  >(undefined);
  const [products, setProducts] = useState<PlanProps[]>([]);
  const mainRef = useRef<null | HTMLDivElement>(null);
  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [promoCode, setPromoCode] = useState<string>();
  // To persist the tier 1 data for chart in three heights plan
  const [energySavings, setEnergySavings] = useState(0);
  const [errorModalContent, setErrorModalContent] = useState<any>(undefined);
  const mediaQuery = useMediaQuery("(max-width:430px)");
  const isComparePage = location.pathname === "/";
  const [dmPlans, setDmPlans] = useState<any>();

  const { ipZipCode, isFetching } = useIpZipCode();
  useEffect(() => {
    // For AC Protection Plan Grid No Zipcode Input
    const promocode = location.pathname.substring(1);
    if (promocode === "OFFERKA1") {
      setZipcode("77020");
    } else if (promocode === "OFFERKA4") {
      setZipcode("75201");
    } else {
      if (ipZipCode) {
        setZipcode(ipZipCode);
      }
    }
  }, [ipZipCode]);

  // Set the promocode from the url params
  useEffect(() => {
    setPromoCode((location.pathname.substring(1) as string) || PROMO_CODE);
    // Storing Promocode for retreving in Enrollment Funnel
    localStorage.setItem(
      "promoCode",
      (location.pathname.substring(1) as string) || PROMO_CODE
    );
  }, [location]);

  const isDmPage = useMemo(() => {
    if (DM_CODES.includes(promoCode || "")) {
      return true;
    }
    return false;
  }, [promoCode]);

  useEffect(() => {
    if (zipcode.length === 5 && !loading) {
      setProducts([]);
      setLoading(true);
      setSelectedUtility("");
      setUtilities([]);
      setSelectedTerm("");
      setChartData([]);
      getUtility();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipcode]);

  useEffect(() => {
    if (selectedUtility) {
      setChartData([]);
      setPlans(undefined);
      setProducts([]);
      getProducts();
      getPlan();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerm]);

  const getUtility = () => {
    fetchUtility(zipcode).then((res) => {
      if (res && res[0] && res[0].utility_code) {
        setUtilities(res);

        if (res[0] && res[0].utility_code) {
          if (res.length > 1) {
            return setLoading(false);
          }
          setSelectedUtility(res[0].utility_name);
        }
      } else {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    if (!selectedUtility) return;
    setSelectedTerm("12");

  }, [selectedUtility]);

  const handleScroll = () => {
    if (mainRef && mainRef.current) {
      mainRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const findUtilityCode = () => {
    if (!selectedUtility) return "";
    return utilities.filter((val) => val.utility_name === selectedUtility)[0]
      .utility_code;
  };

  const getProducts = (myUsage?: Number[]) => {
    setLoading(true);
    const utilityCode = findUtilityCode();
    const term = selectedTerm ? selectedTerm : "12";
    const provider_id = PROVIDER_ID;
    const usages = myUsage ? myUsage : new Array(12).fill(parseInt("1000"));

    fetchConstellationPlans(
      zipcode,
      utilityCode,
      term,
      provider_id,
      usages,
      promoCode
    ).then((res: any) => {
      setLoading(false);
      if (res && Number(res.status) === 1) {
        const { plans } = res.response;
        if (plans) {
          var _plans = plans.filter(
            (item: any) => item.plan_name.indexOf("Bill Credit") > 0
          );
          setProducts(_plans);
        }
        if (res.response.utility) {
          localStorage.setItem("utility", JSON.stringify(res.response.utility));
        }
      } else {
        setProducts([]);
      }
    });
  };

  const getPlan = (myUsage?: Number[]) => {
    const usages = myUsage
      ? myUsage
      : new Array(Number(selectedTerm ? selectedTerm : "12")).fill(
          parseInt("1000")
        );

    const utilityCode = findUtilityCode();

    setLoading(true);
    setChartLoading(true);
    fetchPlan(
      parseInt(selectedTerm),
      utilityCode,
      usages
    ).then((res) => {
      setLoading(false);
      if (res && res.code === 1) {
        const allPlans = res.response;
        setPlans(allPlans);
      }
    });
  };

  useEffect(() => {
    getDmPlans();
  }, [products, plans]);

  useEffect(() => {
    if (dmPlans && dmPlans.length > 0) {
      let list = [];
      if (promoCode === "OFFERKA1") {
        list = dmPlans.filter((plan:any) => plan.provider_id === "196" || plan.provider_id === "159")
        
      }else if (promoCode === 'OFFERKA4') {
        list = dmPlans.filter((plan:any) => plan.provider_id === "196" || plan.provider_id === "176")
      }
      const rateDiff = (parseFloat(list[1].price1000) *10) - (parseFloat(list[0].price1000) *10)
      setEnergySavings((rateDiff * parseFloat("1000") * parseInt(selectedTerm)) / 1000)
    }
  }, [dmPlans]);

  const getDmPlans = () => {
    if (products && products.length > 0 && plans) {
      let providerPlan = [];
      for (let i in plans) {
        const _plans = plans[i];
        let filteredPlans;
      
        if (i === "176") {
          filteredPlans = _plans.filter((item) => (
            item.plan_name === "Saver's Choice 12" 
          ));
        } else {
          filteredPlans = _plans.filter((item) => (
            item.rate_type === "Fixed" &&
            item.contract_term.toString() === selectedTerm &&
            item.provider_id !== "196"
          ));
        }
      
        if (filteredPlans.length > 0) {
          providerPlan.push(filteredPlans[0]);
        }
      }
      let comparePlans = products.filter((item)=>item.contract_term.toString() === selectedTerm).slice(0, 1);
      setDmPlans(comparePlans.concat(providerPlan));
      setChartLoading(false);
    }
  };

  return (
    <SectionWrapper>
      <img
        src="images/swoosh.png"
        alt=""
        height={10}
        width="40%"
        className="background-img"
      />
      <EntrySection className="MuiGrid-container" isCompare={isComparePage}>
        <Container>
          <Grid
            container
            position="relative"
            alignItems="center"
            justifyContent={"space-between"}
            sx={{
              pt: 3,
              pb: 3,
              pl: 6,
              pr: 6,
              backgroundColor: "#fff",
              borderRadius: 2,
            }}
          >
            <Grid
              item
              xs={12}
              sm={5}
              position="relative"
              zIndex="1"
              textAlign="left"
              pr={{ md: 4 }}
              pb={{ xs: 3, sm: 3 }}
              pt={{ md: 2 }}
            >
              <>
                <h2 style={{ marginTop: 0, fontWeight: 700 }}>
                  See if you can save on your electricity bill
                </h2>
                <p>
                  Switch to Constellation today and get up to $700 in bonuses!
                </p>
              </>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              textAlign="right"
              sx={{
                display:
                  mediaQuery && location.pathname !== "/" ? "none" : "block",
              }}
            >
              <img src="/images/Tango.png" width="100%" height="100%" alt="" />
            </Grid>
          </Grid>
        </Container>
      </EntrySection>
      <img src="images/swoosh.png" alt="" height={20} width="100%" />
      <ChartSection>
        {(loading || chartLoading) && (
          <CircularProgress style={{ margin: "100px 0" }} />
        )}
        <>
          {selectedUtility && selectedTerm && !chartLoading && (
            <ACGridContainer ref={mainRef}>
              <h2 style={{ marginBottom: "15px" }}>
                Estimated Total Savings ={" "}
                <RollingNumber
                  data={energySavings}
                  styles={{ color: "#2372B9", fontWeight: 700 }}
                />
              </h2>
              <p
                style={{
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: 400,
                  marginBottom: "15px",
                }}
              >
                Savings based on highest rate shown in the comparison below.
              </p>
              <DMGrid
                products={products}
                dmPlans={dmPlans}
                setOneRepPlanList={setOneRepPlanList}
                showModal={showModal}
                setShowModal={() => setShowModal(true)}
                promoCode={promoCode}
              />
            </ACGridContainer>
          )}
        </>
      </ChartSection>
      <HomeIntroSection>
        <Container>
          <img src="images/best_texas_2024_badge.png" alt="" />
          <HomeIntroContentSection>
            <p>Did you know?</p>
            <p>
              Constellation was selected as the{" "}
              <strong>Best Texas Provider</strong> by ElectricityRates.com in
              2023 and 2024.
            </p>
          </HomeIntroContentSection>
        </Container>
      </HomeIntroSection>
      <IntroSection color="#fff">
        <Container>
          <Grid
            container
            position="relative"
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid
              item
              xs={12}
              sm={6}
              position="relative"
              zIndex="1"
              textAlign="center"
              order={0}
            >
              <IntroContentSection>
                <h2>Why Switch to Constellation?</h2>
                <p>
                  Constellation offers a variety of home energy options to fit
                  your budget, values and needs. Compare plan benefits and see
                  why over 2 million customers choose Constellation as their
                  trusted energy supplier.
                </p>
              </IntroContentSection>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              position="relative"
              zIndex="1"
              textAlign="center"
              alignSelf={"flex-start"}
              order={1}
            >
              <TickGrid>
                <TickSection style={{ backgroundColor: "#12385D" }}>
                  <img src="/images/icon-tick.png" alt="" />
                  <h3>Fixed Rates</h3>
                  <p>
                    Enjoy price stability for the duration of your plan term
                  </p>
                </TickSection>
                <TickSection style={{ backgroundColor: "#6AA443" }}>
                  <img src="/images/icon-tick.png" alt="" />
                  <h3>Bill Credits</h3>
                  <p>Earn up to $50 back on your monthly bill</p>
                </TickSection>
              </TickGrid>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              position="relative"
              zIndex="1"
              textAlign="center"
              alignSelf={"flex-end"}
              order={{ xs: 4, sm: 3 }}
            >
              <TickGrid>
                <TickSection style={{ backgroundColor: "#2073B7" }}>
                  <img src="/images/icon-tick.png" alt="" />
                  <h3>Easy Setup</h3>
                  <p>Enroll online or over the phone in 5 minutes</p>
                </TickSection>
                <TickSection style={{ backgroundColor: "#F37C27" }}>
                  <img src="/images/icon-tick.png" alt="" />
                  <h3>ETF Coverage</h3>
                  <p>We will cover your early termination fee up to $150</p>
                </TickSection>
              </TickGrid>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              position="relative"
              zIndex="1"
              textAlign="center"
              alignSelf={"flex-end"}
              order={{ xs: 3, sm: 4 }}
            >
              <IntroContentSection marginTop={"-80px"}>
                <img src={"images/best_texas_2024_badge.png"} height={160} />
              </IntroContentSection>
            </Grid>
          </Grid>
        </Container>
      </IntroSection>
      <ChoiceSection>
        <Container>
          <h2>The Power of Choice</h2>
          <p>
            At Constellation, we take pride in offering a wide selection of
            energy plans to meet the diverse needs of our customers. However, it
            is important to note that the plans you see on this landing page do
            not represent all plans available in your market. The comparisons
            you see on here are against companies that we believe meet our
            standard of quality, but we encourage you to continue your research
            and due diligence to find the plan that best fits your needs.
          </p>
          <Button
            onClick={() => {
              handleScroll();
            }}
            style={{background:"none"}}
            variant="contained"
            className="default-button"
          >
            Back to Plans
          </Button>
        </Container>
      </ChoiceSection>
      <EvaluateSection>
        <Container>
          <h3 style={{ fontWeight: "600" }}>
            See what our customers are saying
          </h3>
          <GoogleSwiper />
          <img src="/images/google_review.png" alt="" height={150} />
        </Container>
      </EvaluateSection>
      <PlanModal
        show={showModal}
        data={oneRepPlanList}
        handleClose={() => {
          setShowModal(false);
        }}
        selectProduct={() => {}}
        promoCode={promoCode}
        avgMonthlyUse={"1000"}
      />
      <ErrorModal
        content={errorModalContent}
        handleClose={() => setErrorModalContent(undefined)}
      />
    </SectionWrapper>
  );
};

export default withSnackbar(DmPage);
